import * as React from "react"
import { isEqual } from "../utils/isEqual"
import { ReactNode } from "react"
import { resetSetStyle } from "../utils/useWebkitFixes"
import { elementForComponent } from "../utils/elementForComponent"
import { MotionStyle } from "framer-motion"
import { forceLayerBackingWithMotionStyle, forceLayerBackingWithCSSProperties } from "../utils/setLayerBacked"
import type { Rect } from "../types/Rect"

/** @public */
export interface IdentityProps {
    /** An unique id for the layer */
    id?: string
    duplicatedFrom?: string[]
}

export interface WillChangeTransformProp {
    willChangeTransform?: boolean
}

/** @public */
export interface DOMLayoutProps {
    /** @internal */
    _needsMeasure?: boolean
    /** @internal */
    _usesDOMRect?: boolean
    /** @internal */
    _domRect?: Rect | null
}

/** @public */
export interface LayerProps extends IdentityProps, WillChangeTransformProp, DOMLayoutProps {
    children?: ReactNode
    key?: React.Key
    /** @internal: a stable identifier to use when generating layoutIds based on duplicatedFrom. */
    layoutIdKey?: string
    /** @internal: the prefixed node id, used for the forwarded overrides look up */
    _forwardedOverrideId?: string
    /** @internal */
    _forwardedOverrides?: { [key: string]: any }
    /** @internal */
    _index?: number
    /** @internal */
}
/**
 * @public
 */
export class Layer<P extends Partial<LayerProps>, S> extends React.Component<P, S> {
    static readonly defaultProps: LayerProps = {}

    static applyWillChange(props: WillChangeTransformProp, style: MotionStyle, usingMotionStyle: boolean) {
        if (props.willChangeTransform) {
            if (usingMotionStyle) {
                forceLayerBackingWithMotionStyle(style)
            } else {
                forceLayerBackingWithCSSProperties(style as React.CSSProperties)
            }
        }
    }

    /** @internal */
    shouldComponentUpdate(nextProps: P, nextState: S) {
        return nextProps._needsMeasure || this.state !== nextState || !isEqual(this.props, nextProps)
    }

    props: Readonly<P> & Readonly<{ children?: ReactNode }>

    /** @internal */
    componentDidUpdate(prevProps: P) {
        // Workarounds for WebKit bugs

        // Some styles have to be toggled to take effect in certain situations.
        // Not using type safety, uses lots of internal knowledge for efficiency
        // To use this as a hook, see useWebKitFixes

        const element = elementForComponent(this)

        if (this.props["clip"] && this.props["radius"] === 0 && prevProps["radius"] !== 0) {
            resetSetStyle(element, "overflow", "hidden", false)
        }
    }
}
